import React, { useEffect, useRef, useState } from "react";
import { CentsPalette } from "../../../../utils/colors";
import InputFilter from "./filters/InputFilter";
import GenericFilter from "./filters/GenericFilter";
import PicklistFilter from "./filters/PicklistFilter";
import useFilters from "../../../../hooks/useFilters";
import { filterKeys } from "../../../../enums/filter-key";
import { FilledButton } from "../../common/buttons/FilledButton";
import { OutlinedButton } from "../../common/buttons/OutlinedButton";
import { CustomOutlinedButton } from "../../common/buttons/CustomOutlinedButton";
import DateFilter from "./filters/DateFilter";
import SearchBar from "./filters/SearchBarFilter";
import { type_picklist, status_picklist } from "../../../../assets/filters/picklist-items";
import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { translations } from "../../../../utils/translations/translations-manager";


interface Props {
  filterTable: () => void,
  appliedFilters: Record<string, any>
}

const ContractsFilters: React.FC<Props> = ({ filterTable, appliedFilters }) => {
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const { getFilterItem, removeFilter, removeAllFilters, getValueLabel } = useFilters()

  const handleActivateFilter = () => {
    filterTable()
    setIsPopupOpen(false)
  }

  const handleRemoveAllFilters = () => {
    removeAllFilters()
    filterTable()
    setIsPopupOpen(false)
  }

  return (
    <div className="filters flex items-start gap-5 flex-wrap">

      <div className='filter-section flex gap-5 flex-wrap'>
        <SearchBar placeholder={translations.texts.search.toString() + "..."} filterKey={filterKeys.fullText} filterTable={filterTable} />

        <CustomOutlinedButton onClick={() => setIsPopupOpen(true)} className="filter-button h-10 no-wrap gap-2 text-sm font-semibold border-cents-purple text-cents-purple">
          {translations.texts.filter.toString()}
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 6L21 6M3 6L12 6M3 18L12 18M3 12H8M12 12H21M16 18H21M16 4V8M8 10V14M16 16V20" stroke={CentsPalette.cents_purple} strokeWidth="2" strokeLinecap="round" />
          </svg>
        </CustomOutlinedButton>
      </div>

      {Object.keys(appliedFilters).length !== 0 && (
        <div className="filter-tag-container flex-1 flex flex-wrap gap-2">
          {Object.entries(appliedFilters).map(([key, value]) => (
            <div key={key} className={`filters-tag h-10 w-fit px-4 flex justify-between items-center bg-cents-grey gap-5 rounded-2xl`}>
              <p className="text-cents-black whitespace-nowrap">
                {getFilterItem(key).label}:
                <span className="font-medium ml-1">
                  {key === 'startAmount' || key === 'endAmount' ? value / 100
                    : key === 'startDate' || key === 'endDate' ? (new Date(value)).toLocaleDateString(translations.getLanguage())
                      : key === 'status' ? getValueLabel(status_picklist, value)
                        : key === 'type' ? getValueLabel(type_picklist, value)
                          : value}
                </span>
              </p>
              <button
                onClick={() => {
                  removeFilter(key)
                  filterTable()
                }}
                className="focus:outline-none"
              >
                <svg className="h-3 w-3" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4L36 36M36 4L4 36" stroke={CentsPalette.cents_purple} strokeLinecap="round" strokeLinejoin="round" strokeWidth="6" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={`filter-popup ${isPopupOpen ? 'flex' : 'hidden'} z-30 absolute inset-0 w-[calc(100%-40px)] h-[calc(100%-40px)] min-h-fit p-5 m-5 bg-cents-black bg-opacity-30 rounded-2xl`}>
        <div className="m-20 p-5 pb-10 w-[calc(100%-160px)] h-fit flex flex-col gap-14 backdrop-blur bg-cents-white bg-opacity-60 rounded-2xl">
          <div className="filter-actions flex justify-between gap-3 flex-wrap-reverse">
            <div className="flex gap-3 w-fit flex-1">
              <FilledButton onClick={handleActivateFilter} label={translations.texts.apply_filters.toString()} className="w-fit flex-1 xl:max-w-fit  whitespace-nowrap" />
              <OutlinedButton onClick={handleRemoveAllFilters} label={translations.texts.remove_all.toString()} className="w-fit flex-1 xl:max-w-fit border-cents-purple text-cents-purple whitespace-nowrap" />
            </div>
            <CustomOutlinedButton onClick={() => setIsPopupOpen(false)} className="back-button flex-1 xl:max-w-fit flex items-center h-10 text-cents-black border-cents-black">
              <ArrowUturnLeftIcon className="h-5 w-5 mr-3" strokeWidth={2} />
              <span className="whitespace-nowrap">{translations.texts.back_to_donations.toString()}</span>
            </CustomOutlinedButton>
          </div>
          <div className="filter-container max-w-full flex flex-wrap justify-center gap-10">
            <GenericFilter
              filter={getFilterItem(filterKeys.fullName)}
              closePopup={() => setIsPopupOpen(false)}
              filterTable={filterTable}
            >
              <InputFilter filterKey={filterKeys.fullName} placeholder={translations.texts.insert.toString() + " " + translations.texts.name_and_surname.toString()} />
            </GenericFilter>
            <GenericFilter
              filter={getFilterItem(filterKeys.contractId)}
              closePopup={() => setIsPopupOpen(false)}
              filterTable={filterTable}
            >
              <InputFilter placeholder={translations.texts.insert.toString() + " " + translations.texts.contract_id.toString()} filterKey={filterKeys.contractId} />
            </GenericFilter>
            <GenericFilter
              filter={getFilterItem(filterKeys.operatorId)}
              closePopup={() => setIsPopupOpen(false)}
              filterTable={filterTable}
            >
              <InputFilter placeholder={translations.texts.insert.toString() + " " + translations.texts.operator_code.toString()} filterKey={filterKeys.operatorId} />
            </GenericFilter>
            <GenericFilter
              filter={getFilterItem(filterKeys.campaignId)}
              closePopup={() => setIsPopupOpen(false)}
              filterTable={filterTable}
            >
              <p className="text-gray-500 text-left text-sm bg-cents-white h-10 w-[302px] pl-4 inline-flex items-center rounded-2xl">{translations.texts.coming_soon.toString()}...</p>
              {/*<InputFilter placeholder={'Inserisci campaignId'} filterKey={filterKeys.campaignId} />*/}
            </GenericFilter>
            <GenericFilter
              filter={getFilterItem(filterKeys.type)}
              closePopup={() => setIsPopupOpen(false)}
              filterTable={filterTable}
            >
              <PicklistFilter filterKey={filterKeys.type} picks={type_picklist} />
            </GenericFilter>
            <GenericFilter
              filter={getFilterItem(filterKeys.status)}
              closePopup={() => setIsPopupOpen(false)}
              filterTable={filterTable}
            >
              <PicklistFilter picks={status_picklist} filterKey={filterKeys.status} />
            </GenericFilter>
            <GenericFilter
              filter={getFilterItem(filterKeys.paymentType)}
              closePopup={() => setIsPopupOpen(false)}
              filterTable={filterTable}
            >
              <p className="text-gray-500 text-left text-sm bg-cents-white h-10 w-[302px] pl-4 inline-flex items-center rounded-2xl">{translations.texts.coming_soon.toString()}...</p>
              {/*<PicklistFilter picks={payment_methods_options} filterKey={filterKeys.paymentType} />*/}
            </GenericFilter>

            <div className="amount-range-filter flex flex-wrap gap-y-5 gap-x-10 justify-center items-center bg-cents-grey rounded-2xl">
              <GenericFilter
                filter={getFilterItem(filterKeys.startAmount)}
                closePopup={() => setIsPopupOpen(false)}
                filterTable={filterTable}
              >
                <InputFilter placeholder={translations.texts.insert.toString() + " " + translations.texts.start_amount.toString()} filterKey={filterKeys.startAmount} currency />
                {/*<AmountSliderFilter filterKeyStart={filterKeys.startAmount} filterKeyEnd={filterKeys.endAmount} min={0} max={100} />*/}
              </GenericFilter>
              <GenericFilter
                filter={getFilterItem(filterKeys.endAmount)}
                closePopup={() => setIsPopupOpen(false)}
                filterTable={filterTable}
              >
                <InputFilter placeholder={translations.texts.insert.toString() + " " + translations.texts.end_amount.toString()} filterKey={filterKeys.endAmount} currency />
                {/*<AmountSliderFilter filterKeyStart={filterKeys.startAmount} filterKeyEnd={filterKeys.endAmount} min={0} max={100} />*/}
              </GenericFilter>
            </div>

            <div className="date-range-filter flex flex-wrap gap-y-5 gap-x-10 justify-center items-center bg-cents-grey rounded-2xl">
              <GenericFilter
                filter={getFilterItem(filterKeys.startDate)}
                closePopup={() => setIsPopupOpen(false)}
                filterTable={filterTable}
              >
                <DateFilter filterKey={filterKeys.startDate} placeholder={translations.texts.insert.toString() + " " + translations.texts.start_date.toString()} initialDate={new Date()} />
              </GenericFilter>
              <GenericFilter
                filter={getFilterItem(filterKeys.endDate)}
                closePopup={() => setIsPopupOpen(false)}
                filterTable={filterTable}
              >
                <DateFilter filterKey={filterKeys.endDate} placeholder={translations.texts.insert.toString() + " " + translations.texts.end_date.toString()} initialDate={new Date()} />
              </GenericFilter>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContractsFilters